/**
 * Ticket Lifecycle v2 — Slice 5D-3
 *
 * React Query hook that produces per-advisor × per-bucket metrics by
 * stitching together three data slices and feeding the pure
 * `computeAdvisorBucketMetrics` helper:
 *
 *   1. `advisor_action_events` filtered to this advisor's
 *      `ticket_accepted` events in the last 30 days. The metadata
 *      payload captured at slice 5D-2 carries
 *      `remaining_approved_work_minutes` per accepted ticket.
 *   2. `ticket_custody_events` for the same ticket set — to compute
 *      per-cycle acceptLag / FOH-custody / advisor-lifecycle minutes.
 *   3. `tickets` (for `is_comeback`) + `ticket_jobs` (for diag/work
 *      classification) — to bucket each accepted ticket via
 *      `classifyTicketJobMix`.
 *
 * One slice per accepted ticket, deduped by `ticket_id` (first
 * `ticket_accepted` event wins) so `ticketSampleSize` is unambiguous.
 *
 * Gated by `advisorTimingAnalytics` (default OFF in every preset).
 * When the flag is off the query is `enabled: false` — zero network,
 * zero cost.
 *
 * Boundary: reads custody / advisor-action / ticket-jobs tables. Does
 * NOT import scheduling / timer / parking / rollover / bay / invariants
 * modules. Drift detector boundary guard pins this.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import {
  computeAdvisorBucketMetrics,
  type AdvisorBucketMetricsResult,
  type AdvisorBucketTicketSlice,
} from '@/lib/tickets/lifecycle/advisorBucketMetrics';
import {
  classifyTicketJobMix,
  type TicketJobMixBucket,
} from '@/lib/tickets/lifecycle/jobMixClassifier';
import { ADVISOR_METRICS_WINDOW_DAYS } from '@/lib/tickets/lifecycle/advisorMetrics';

interface UseAdvisorBucketMetricsArgs {
  shopId: string | null | undefined;
  advisorUserId: string | null | undefined;
}

const WINDOW_MS = ADVISOR_METRICS_WINDOW_DAYS * 24 * 60 * 60 * 1000;

interface ActionEventRow {
  ticket_id: string;
  created_at: string;
  metadata: Record<string, unknown> | null;
}

interface CustodyEventRow {
  event_kind: string;
  ticket_id: string;
  actor_id: string | null;
  created_at: string;
}

interface TicketRow {
  id: string;
  is_comeback: boolean | null;
}

interface JobRow {
  ticket_id: string;
  category: string | null;
  submenu: string | null;
  proficiency_category: string | null;
  name: string | null;
  jobs: {
    name: string | null;
    category: string | null;
    submenu: string | null;
    proficiency_category: string | null;
  } | null;
}

/**
 * Pure helper: given the four-event sequence around a ticket_accepted
 * timestamp, compute the per-cycle interval durations. Walks events
 * sorted ascending and finds the cycle whose `foh_accept` was
 * performed by `advisorUserId` and is closest to `acceptedAtMs`.
 *
 * Exported for testing in isolation.
 */
export function extractCycleDurations(
  events: ReadonlyArray<CustodyEventRow>,
  advisorUserId: string,
  acceptedAtMs: number,
): {
  acceptLagMinutes: number | null;
  fohCustodyMinutes: number | null;
  advisorLifecycleMinutes: number | null;
} {
  // Sort events ascending by created_at for deterministic walk.
  const sorted = [...events].sort((a, b) =>
    a.created_at.localeCompare(b.created_at),
  );
  let cycleStartMs: number | null = null;
  let acceptedMs: number | null = null;
  let kickToBohMs: number | null = null;
  let bohAcceptMs: number | null = null;
  let attributed = false;

  for (const ev of sorted) {
    const ms = Date.parse(ev.created_at);
    if (!Number.isFinite(ms)) continue;
    switch (ev.event_kind) {
      case 'kick_to_foh':
        // New cycle; if we already attributed a prior cycle, keep its
        // values until a closer one is found. We choose the cycle
        // whose foh_accept matches `acceptedAtMs` exactly.
        cycleStartMs = ms;
        acceptedMs = null;
        kickToBohMs = null;
        bohAcceptMs = null;
        attributed = false;
        break;
      case 'foh_accept':
        if (ev.actor_id === advisorUserId && ms === acceptedAtMs) {
          attributed = true;
          acceptedMs = ms;
        } else if (attributed && acceptedMs !== null) {
          // A different advisor took over inside the same cycle; the
          // original attribution still holds for the metrics we
          // already captured pre-handoff. Leave state as-is.
        } else {
          attributed = false;
        }
        break;
      case 'kick_to_boh':
        if (attributed && acceptedMs !== null && kickToBohMs === null) {
          kickToBohMs = ms;
        }
        break;
      case 'boh_accept':
        if (attributed && acceptedMs !== null && bohAcceptMs === null) {
          bohAcceptMs = ms;
        }
        break;
    }
    // Once both halves of the attributed cycle are known, we can stop.
    if (attributed && kickToBohMs !== null && bohAcceptMs !== null) break;
  }

  const acceptLagMinutes =
    cycleStartMs !== null && acceptedMs !== null && acceptedMs >= cycleStartMs
      ? (acceptedMs - cycleStartMs) / 60_000
      : null;
  const fohCustodyMinutes =
    acceptedMs !== null && kickToBohMs !== null && kickToBohMs >= acceptedMs
      ? (kickToBohMs - acceptedMs) / 60_000
      : null;
  const advisorLifecycleMinutes =
    cycleStartMs !== null && bohAcceptMs !== null && bohAcceptMs >= cycleStartMs
      ? (bohAcceptMs - cycleStartMs) / 60_000
      : null;

  return { acceptLagMinutes, fohCustodyMinutes, advisorLifecycleMinutes };
}

/**
 * Pure helper: pluck the slice-5D-2 metadata key as a finite number.
 * Anything else (missing key, null, non-finite, wrong type) → null.
 *
 * Exported for testing.
 */
export function readRemainingApprovedWorkFromMetadata(
  metadata: Record<string, unknown> | null,
): number | null {
  if (!metadata) return null;
  const raw = metadata['remaining_approved_work_minutes'];
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  return null;
}

/**
 * Slice 5D-14 Batch 2.5: pure async fetcher extracted from the
 * single-advisor hook so the same logic can be called per-advisor
 * inside `useQueries` (board-level predicted bay-hold fan-out)
 * WITHOUT duplicating the four-step DB pipeline. Returns the same
 * `AdvisorBucketMetricsResult` shape and uses an identical query
 * key shape, so cache is shared with `useAdvisorBucketMetrics`.
 *
 * The function is intentionally NOT gated by feature flags —
 * callers (the hook above, or the board-level useQueries) decide
 * via `enabled` whether to invoke it. This matches the standard
 * React-Query queryFn contract: the queryFn assumes inputs are
 * valid and trusts the caller's gating.
 */
export async function fetchAdvisorBucketMetrics(params: {
  shopId: string;
  advisorUserId: string;
  windowStartIso: string;
}): Promise<AdvisorBucketMetricsResult> {
  const { shopId, advisorUserId, windowStartIso: windowStart } = params;
  // 1. ticket_accepted events for this advisor in window.
  const { data: actionRows, error: actionErr } = await (supabase as any)
    .from('advisor_action_events')
    .select('ticket_id, created_at, metadata')
    .eq('shop_id', shopId)
    .eq('advisor_user_id', advisorUserId)
    .eq('action_kind', 'ticket_accepted')
    .gte('created_at', windowStart);
  if (actionErr) {
    console.error('[advisor-bucket-metrics] action events query failed', actionErr);
    throw actionErr;
  }
  const actionEvents = (actionRows ?? []) as ActionEventRow[];

  // Dedupe by ticket_id — first ticket_accepted wins. Keeps
  // `ticketSampleSize` literally per-ticket.
  const acceptedByTicket = new Map<string, ActionEventRow>();
  for (const ev of actionEvents) {
    if (!acceptedByTicket.has(ev.ticket_id)) {
      acceptedByTicket.set(ev.ticket_id, ev);
    }
  }
  const ticketIds = Array.from(acceptedByTicket.keys());
  if (ticketIds.length === 0) {
    return computeAdvisorBucketMetrics({
      advisorUserId: advisorUserId as string,
      tickets: [],
    });
  }

  // 2. Custody events for the same ticket set + four event kinds.
  const { data: custodyRows, error: custodyErr } = await (supabase as any)
    .from('ticket_custody_events')
    .select('event_kind, ticket_id, actor_id, created_at')
    .eq('shop_id', shopId)
    .in('ticket_id', ticketIds)
    .in('event_kind', ['kick_to_foh', 'foh_accept', 'kick_to_boh', 'boh_accept']);
  if (custodyErr) {
    console.error('[advisor-bucket-metrics] custody events query failed', custodyErr);
    throw custodyErr;
  }
  const custodyEvents = (custodyRows ?? []) as CustodyEventRow[];
  const custodyByTicket = new Map<string, CustodyEventRow[]>();
  for (const ev of custodyEvents) {
    const list = custodyByTicket.get(ev.ticket_id) ?? [];
    list.push(ev);
    custodyByTicket.set(ev.ticket_id, list);
  }

  // 3. Tickets (is_comeback) and 4. ticket_jobs (classification).
  const [{ data: ticketsRows, error: ticketsErr }, { data: jobsRows, error: jobsErr }] =
    await Promise.all([
      (supabase as any)
        .from('tickets')
        .select('id, is_comeback')
        .in('id', ticketIds),
      (supabase as any)
        .from('ticket_jobs')
        .select(
          'ticket_id, category, submenu, proficiency_category, name, jobs:job_id(name, category, submenu, proficiency_category)',
        )
        .in('ticket_id', ticketIds)
        .is('deleted_at', null),
    ]);
  if (ticketsErr) {
    console.error('[advisor-bucket-metrics] tickets query failed', ticketsErr);
    throw ticketsErr;
  }
  if (jobsErr) {
    console.error('[advisor-bucket-metrics] ticket_jobs query failed', jobsErr);
    throw jobsErr;
  }
  const ticketsById = new Map<string, TicketRow>(
    ((ticketsRows ?? []) as TicketRow[]).map((t) => [t.id, t]),
  );
  const jobsByTicket = new Map<string, JobRow[]>();
  for (const row of (jobsRows ?? []) as JobRow[]) {
    const list = jobsByTicket.get(row.ticket_id) ?? [];
    list.push(row);
    jobsByTicket.set(row.ticket_id, list);
  }

  // Build slices.
  const slices: AdvisorBucketTicketSlice[] = [];
  for (const [ticketId, actionEv] of acceptedByTicket.entries()) {
    const acceptedMs = Date.parse(actionEv.created_at);
    if (!Number.isFinite(acceptedMs)) continue;

    const ticket = ticketsById.get(ticketId);
    const ticketJobs = jobsByTicket.get(ticketId) ?? [];

    // Classification reads top-level row category/submenu/name when
    // present, and falls back to the joined `jobs` catalog fields
    // for shops where ticket_jobs rows don't carry their own
    // category copy. classifyTicketJobMix accepts a flat shape;
    // merge per-job before calling.
    const flatJobs = ticketJobs.map((j) => ({
      category: j.category ?? j.jobs?.category ?? undefined,
      submenu: j.submenu ?? j.jobs?.submenu ?? undefined,
      proficiency_category:
        j.proficiency_category ?? j.jobs?.proficiency_category ?? undefined,
      name: j.name ?? j.jobs?.name ?? undefined,
    }));

    const classification = classifyTicketJobMix(
      { is_comeback: ticket?.is_comeback ?? false },
      flatJobs,
    );
    const bucket: TicketJobMixBucket = classification.bucket;

    const { acceptLagMinutes, fohCustodyMinutes, advisorLifecycleMinutes } =
      extractCycleDurations(
        custodyByTicket.get(ticketId) ?? [],
        advisorUserId,
        acceptedMs,
      );

    // Per-cycle advisor observed = accept lag + active FOH custody
    // (the time this advisor was responsible). Null when either
    // half is missing — averages skip nulls per metric.
    const advisorObservedMinutes =
      acceptLagMinutes !== null && fohCustodyMinutes !== null
        ? acceptLagMinutes + fohCustodyMinutes
        : null;

    slices.push({
      ticketId,
      bucket,
      advisorObservedMinutes,
      acceptLagMinutes,
      fohCustodyMinutes,
      advisorLifecycleMinutes,
      remainingApprovedWorkMinutes: readRemainingApprovedWorkFromMetadata(
        actionEv.metadata,
      ),
    });
  }

  return computeAdvisorBucketMetrics({
    advisorUserId,
    tickets: slices,
  });
}

/**
 * Window-start ISO timestamp shared between the single-advisor hook
 * and the board-level fan-out so they hit the same React-Query
 * cache key.
 *
 * NOTE: this is intentionally module-scoped + capped to the nearest
 * hour-bucket so the value is stable for one hour and re-renders
 * within the bucket don't generate new queryKey arrays. The
 * computation is deterministic per hour boundary.
 */
export function getAdvisorBucketMetricsWindowStartIso(): string {
  return new Date(Date.now() - WINDOW_MS).toISOString();
}

export function useAdvisorBucketMetrics({
  shopId,
  advisorUserId,
}: UseAdvisorBucketMetricsArgs) {
  const advisorTimingAnalyticsEnabled = useFeatureFlag('advisorTimingAnalytics');
  const enabled =
    advisorTimingAnalyticsEnabled && !!shopId && !!advisorUserId;

  const windowStart = useMemo(getAdvisorBucketMetricsWindowStartIso, []);

  return useQuery({
    queryKey: ['advisor-bucket-metrics', shopId, advisorUserId, windowStart],
    enabled,
    retry: false,
    queryFn: () =>
      fetchAdvisorBucketMetrics({
        shopId: shopId as string,
        advisorUserId: advisorUserId as string,
        windowStartIso: windowStart,
      }),
  });
}
